import * as THREE from 'three/webgpu';
import type {SketchEntitiesTransformGeometryService} from './SketchEntitiesTransformGeometryService';
import {disposeSketchEntitiesTransformCloneDomainResources} from './utils/disposeSketchEntitiesTransformCloneDomainResources';

/**
 * 复制实体预览副本域会话。
 *
 * 职责边界：
 * 1. 只持有一次复制产生的预览副本，并在连续鼠标移动期间复用这些对象；
 * 2. 调用方传入的是相对源实体的绝对位移，本类只把相邻两次绝对位移之差写入副本；
 * 3. 会话结束时统一把副本移出场景，并在当前渲染帧结束后释放独占渲染资源；
 * 4. 正式副本创建、历史事务和控制器阶段切换不属于本类职责。
 */
export class SketchCopyEntitiesPreviewSession {
    /** 已经写入当前副本域的绝对位移。 */
    private readonly appliedTranslation = new THREE.Vector3();
    /** 相邻两次绝对位移之间的差值，作为鼠标热路径复用对象。 */
    private readonly translationDelta = new THREE.Vector3();
    /** 会话是否已经完成清理。 */
    private disposed = false;

    /**
     * @param entities 已完成关系与持久身份重建的预览副本根对象。
     * @param geometryService 草图实体变换几何服务。
     */
    public constructor(
        private readonly entities: readonly THREE.Object3D[],
        private readonly geometryService: SketchEntitiesTransformGeometryService,
    ) {}

    /**
     * 读取当前会话持有的预览副本根对象。
     *
     * @returns 只读预览副本集合。
     */
    public getEntities(): readonly THREE.Object3D[] {
        return this.entities;
    }

    /**
     * 把预览副本更新到相对源实体的目标绝对位移。
     *
     * 设计说明：
     * 1. 控制器始终根据 From 点和当前鼠标点计算绝对位移，避免把鼠标采样误差逐帧累加；
     * 2. 副本本身已经位于上一帧目标位置，因此这里只应用“目标绝对位移 - 已应用绝对位移”；
     * 3. 继续复用无约束求解的对象级平移入口，保证圆弧、椭圆、槽口和闭合集合保持刚体定义。
     *
     * @param translation 相对源实体的目标绝对位移。
     */
    public applyTranslation(translation: THREE.Vector3): void {
        if (this.disposed) {
            throw new Error('更新复制实体预览失败：预览会话已经释放。');
        }

        // 只计算本帧需要补充的位移，禁止重新创建副本域或重建关系与持久身份。
        this.translationDelta.copy(translation).sub(this.appliedTranslation);
        if (this.translationDelta.lengthSq() <= 1e-20) {
            return;
        }

        this.geometryService.applyMatrixToEntitiesWithoutConstraintSolve(
            this.entities,
            this.geometryService.createTranslationMatrix(this.translationDelta),
        );
        this.appliedTranslation.copy(translation);
    }

    /**
     * 清理预览副本域及其独占渲染资源。
     *
     * 该方法允许被取消、确认、预览失效和控制器销毁链路重复调用，
     * 幂等保护保证同一 geometry/material 最多进入一次释放队列。
     */
    public dispose(): void {
        if (this.disposed) {
            return;
        }
        this.disposed = true;

        // 先把整套副本移出场景，避免释放队列等待期间仍参与选择、捕捉或渲染。
        this.entities.forEach((entity) => {
            entity.parent?.remove(entity);
        });
        disposeSketchEntitiesTransformCloneDomainResources(this.entities);
    }
}
